import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  GlobalStyles,
  Paper,
  Portal,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

export type GuideStep = {
  selector: string;
  title: string;
  description: string;
  preferredPlacement?: 'auto' | 'top-right' | 'overlay-top-center' | 'overlay-top-left';
  panelSize?: 'regular' | 'compact';
};

interface InteractiveGuideProps {
  open: boolean;
  steps: GuideStep[];
  onClose: () => void;
  labels: {
    previous: string;
    next: string;
    finish: string;
    stepOf: (current: number, total: number) => string;
  };
}

const ACTIVE_CLASS = 'interactive-guide-target-active';
const GUIDE_MIN_WIDTH = 300;
const GUIDE_MAX_WIDTH = 420;
const GUIDE_MIN_WIDTH_COMPACT = 240;
const GUIDE_MAX_WIDTH_COMPACT = 320;

type GuidePosition = {
  top: number;
  left: number;
  width: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getGuidePosition = (
  targetRect: DOMRect,
  panelHeight = 220,
  placement: 'auto' | 'top-right' | 'overlay-top-center' | 'overlay-top-left' = 'auto',
  widthBounds: { minWidth: number; maxWidth: number } = {
    minWidth: GUIDE_MIN_WIDTH,
    maxWidth: GUIDE_MAX_WIDTH,
  }
): GuidePosition => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = 16;
  const gap = 12;

  const width = clamp(viewportWidth - margin * 2, widthBounds.minWidth, widthBounds.maxWidth);
  const estimatedHeight = panelHeight;

  if (placement === 'top-right') {
    return {
      top: margin,
      left: Math.max(margin, viewportWidth - width - margin),
      width,
    };
  }

  if (placement === 'overlay-top-center') {
    const top = clamp(
      targetRect.top + gap,
      margin,
      Math.max(margin, viewportHeight - panelHeight - margin)
    );
    const centeredLeft = targetRect.left + (targetRect.width - width) / 2;
    const left = clamp(centeredLeft, margin, Math.max(margin, viewportWidth - width - margin));

    return {
      top,
      left,
      width,
    };
  }

  if (placement === 'overlay-top-left') {
    const top = clamp(
      targetRect.top + gap,
      margin,
      Math.max(margin, viewportHeight - panelHeight - margin)
    );
    const left = clamp(
      targetRect.left + gap,
      margin,
      Math.max(margin, viewportWidth - width - margin)
    );

    return {
      top,
      left,
      width,
    };
  }

  const hasRightSpace = targetRect.right + gap + width + margin <= viewportWidth;
  const hasLeftSpace = targetRect.left - gap - width - margin >= 0;

  let left = margin;
  let top = margin;

  if (hasRightSpace) {
    left = targetRect.right + gap;
    top = targetRect.top;
  } else if (hasLeftSpace) {
    left = targetRect.left - width - gap;
    top = targetRect.top;
  } else {
    const hasBottomSpace = targetRect.bottom + gap + estimatedHeight + margin <= viewportHeight;
    if (hasBottomSpace) {
      top = targetRect.bottom + gap;
      left = targetRect.left;
    } else {
      top = targetRect.top - estimatedHeight - gap;
      left = targetRect.left;
    }
  }

  const maxTop = Math.max(margin, viewportHeight - estimatedHeight - margin);
  const maxLeft = Math.max(margin, viewportWidth - width - margin);

  return {
    top: clamp(top, margin, maxTop),
    left: clamp(left, margin, maxLeft),
    width,
  };
};

const removeActiveClass = (element: Element | null) => {
  if (element) {
    element.classList.remove(ACTIVE_CLASS);
  }
};

const InteractiveGuide = ({ open, steps, onClose, labels }: InteractiveGuideProps) => {
  const theme = useTheme();
  const [stepIndex, setStepIndex] = useState(0);
  const [availableSteps, setAvailableSteps] = useState<GuideStep[]>([]);
  const [guidePosition, setGuidePosition] = useState<GuidePosition>({
    top: 24,
    left: 24,
    width: GUIDE_MAX_WIDTH,
  });

  const guidePanelRef = useRef<HTMLDivElement | null>(null);
  const activeElementRef = useRef<Element | null>(null);

  const currentStep = useMemo(() => availableSteps[stepIndex], [availableSteps, stepIndex]);

  const updateGuidePosition = useCallback(() => {
    const target = activeElementRef.current as HTMLElement | null;
    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const panelHeight = guidePanelRef.current?.offsetHeight || 220;
    const widthBounds =
      currentStep?.panelSize === 'compact'
        ? { minWidth: GUIDE_MIN_WIDTH_COMPACT, maxWidth: GUIDE_MAX_WIDTH_COMPACT }
        : { minWidth: GUIDE_MIN_WIDTH, maxWidth: GUIDE_MAX_WIDTH };

    setGuidePosition(
      getGuidePosition(rect, panelHeight, currentStep?.preferredPlacement, widthBounds)
    );
  }, [currentStep]);

  useEffect(() => {
    if (!open) {
      removeActiveClass(activeElementRef.current);
      activeElementRef.current = null;
      setAvailableSteps([]);
      setStepIndex(0);
      return;
    }

    const filteredSteps = steps.filter(step => document.querySelector(step.selector));
    setAvailableSteps(filteredSteps);
    setStepIndex(0);

    if (!filteredSteps.length) {
      onClose();
    }
  }, [open, onClose, steps]);

  useEffect(() => {
    let timeoutId: number | null = null;

    removeActiveClass(activeElementRef.current);
    activeElementRef.current = null;

    if (open && currentStep) {
      const targetElement = document.querySelector(currentStep.selector);
      if (targetElement) {
        targetElement.classList.add(ACTIVE_CLASS);
        activeElementRef.current = targetElement;

        (targetElement as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        updateGuidePosition();

        timeoutId = window.setTimeout(() => {
          updateGuidePosition();
        }, 250);
      }
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [open, currentStep, updateGuidePosition]);

  useEffect(() => {
    if (!open || !currentStep) {
      return undefined;
    }

    const rafId = window.requestAnimationFrame(() => {
      updateGuidePosition();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [open, currentStep, stepIndex, updateGuidePosition]);

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const handleViewportChange = () => {
      updateGuidePosition();
    };

    const targetElement = activeElementRef.current as HTMLElement | null;

    if (open && targetElement) {
      resizeObserver = new ResizeObserver(() => {
        updateGuidePosition();
      });
      resizeObserver.observe(targetElement);

      window.addEventListener('resize', handleViewportChange);
      window.addEventListener('scroll', handleViewportChange, true);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, currentStep, updateGuidePosition]);

  const handleClose = () => {
    removeActiveClass(activeElementRef.current);
    activeElementRef.current = null;
    onClose();
  };

  const handlePrevious = () => setStepIndex(prev => Math.max(prev - 1, 0));

  const isLastStep = stepIndex >= availableSteps.length - 1;
  const handleNext = () => {
    if (isLastStep) {
      handleClose();
    } else {
      setStepIndex(prev => prev + 1);
    }
  };

  if (!open || !currentStep || !availableSteps.length) {
    return null;
  }

  return (
    <>
      <GlobalStyles
        styles={{
          [`.${ACTIVE_CLASS}`]: {
            position: 'relative',
            zIndex: 2599,
            boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
            borderRadius: 8,
            transition: 'box-shadow 180ms ease-in-out',
          },
        }}
      />
      <Portal>
        <Backdrop
          open={open}
          sx={{
            zIndex: 2598,
            backgroundColor: 'rgba(0, 0, 0, 0.42)',
          }}
          onClick={handleClose}
        />
        <Box
          ref={guidePanelRef}
          sx={{
            position: 'fixed',
            top: guidePosition.top,
            left: guidePosition.left,
            zIndex: 2600,
            width: guidePosition.width,
            maxWidth:
              currentStep?.panelSize === 'compact' ? GUIDE_MAX_WIDTH_COMPACT : GUIDE_MAX_WIDTH,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: currentStep?.panelSize === 'compact' ? 2 : 2.5,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                {labels.stepOf(stepIndex + 1, availableSteps.length)}
              </Typography>
              <Typography variant="h6">{currentStep.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {currentStep.description}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button onClick={handlePrevious} disabled={stepIndex === 0}>
                  {labels.previous}
                </Button>
                <Button variant="contained" onClick={handleNext}>
                  {isLastStep ? labels.finish : labels.next}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Portal>
    </>
  );
};

export default InteractiveGuide;
